import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { toast } from '@/components/ui/use-toast';
import type { ApiError } from '@/lib/api/error';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { QUERY_PRESETS } from '@/lib/query';
import { queryClient } from '@/lib/queryClient';
import { getDeviceLifecycle, getDeviceProfile, getDeviceProfileHeader, getDeviceStatus, saveDeviceProfile, saveDeviceStatus } from './api';
import { deviceDetailKey } from './key';
import type { SaveDeviceProfileRequest, SaveDeviceStatusRequest } from './type';
export type { DeviceDetailAttribute, DeviceDetailSection, GetDeviceProfileHeaderResponse, DeviceDetailSectionResponse } from './type';
export function useGetDeviceProfile(id: string) {
  return useQuery({
    ...QUERY_PRESETS.detail(),
    queryKey: deviceDetailKey.profile(id),
    queryFn: () => getDeviceProfile(id)
  });
}
export function useGetDeviceProfileHeader(id: string) {
  return useQuery({
    ...QUERY_PRESETS.detail(),
    queryKey: deviceDetailKey.profileHeader(id),
    queryFn: () => getDeviceProfileHeader(id)
  });
}
export function useSuspenseGetDeviceProfileHeader(id: string) {
  return useSuspenseQuery({
    ...QUERY_PRESETS.detail(),
    queryKey: deviceDetailKey.profileHeader(id),
    queryFn: () => getDeviceProfileHeader(id)
  });
}
export function useGetDeviceLifecycle(id: string) {
  return useQuery({
    ...QUERY_PRESETS.detail(),
    queryKey: deviceDetailKey.lifecycle(id),
    queryFn: () => getDeviceLifecycle(id)
  });
}
export function useGetDeviceStatus(id: string) {
  return useQuery({
    ...QUERY_PRESETS.detail(),
    queryKey: deviceDetailKey.status(id),
    queryFn: () => getDeviceStatus(id)
  });
}
export function useSaveDeviceProfile(id: string, options?: {
  onSuccess?: () => void;
  onError?: (error: ApiError) => void;
}) {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.devices.detail'
  });
  return useMutation({
    mutationFn: (data: SaveDeviceProfileRequest) => saveDeviceProfile(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: deviceDetailKey.all
      });
      toast({
        description: t('saveSuccess')
      });
      options?.onSuccess?.();
    },
    onError: (error: ApiError) => {
      options?.onError?.(error);
    }
  });
}
export function useSaveDeviceStatus(id: string, options?: {
  onSuccess?: () => void;
  onError?: (error: ApiError) => void;
}) {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.devices.detail'
  });
  return useMutation({
    mutationFn: (data: SaveDeviceStatusRequest) => saveDeviceStatus(id, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: deviceDetailKey.status(id)
      });
      void queryClient.invalidateQueries({
        queryKey: deviceDetailKey.lifecycle(id)
      });
      toast({
        description: t('saveSuccess')
      });
      options?.onSuccess?.();
    },
    onError: (error: ApiError) => {
      options?.onError?.(error);
    }
  });
}